import { ErrorEnvelopeSchema } from '@aysar/contracts';

import { i18n } from '@/shared/i18n/i18n.config';

import { NETWORK_ERROR_CODE, UNKNOWN_ERROR_CODE, type ApiError } from './transform-error.types';

/**
 * RTK Query's own error shapes, described structurally.
 *
 * Typed here rather than imported from `@reduxjs/toolkit/query` because the
 * union is a moving target across minor versions and this file only needs to
 * narrow four cases. Everything unrecognised falls into the `unknown` branch,
 * which is the branch that must never throw.
 */
type UnknownError = {
  status?: unknown;
  originalStatus?: unknown;
  data?: unknown;
  error?: unknown;
  message?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Only a `Record<string, string[]>` survives — anything else is dropped, not coerced. */
function readDetails(details: unknown): Record<string, string[]> | undefined {
  if (!isRecord(details)) return undefined;

  const entries = Object.entries(details).filter(
    (entry): entry is [string, string[]] =>
      Array.isArray(entry[1]) && entry[1].every((item) => typeof item === 'string'),
  );

  return entries.length === Object.keys(details).length && entries.length > 0
    ? Object.fromEntries(entries)
    : undefined;
}

export function isApiError(value: unknown): value is ApiError {
  return (
    isRecord(value) &&
    typeof value['status'] === 'number' &&
    typeof value['code'] === 'string' &&
    typeof value['message'] === 'string'
  );
}

/**
 * Normalises every failure to one `ApiError`.
 *
 * The api's envelope carries the human-facing text at the TOP level (`message`)
 * and the machine-facing code inside `error` — a shape worth reading exactly
 * once, here, rather than at forty call sites that would each get it subtly
 * wrong.
 *
 * A transport failure is reported as `status: 0` with locally-translated copy,
 * because there is no server message to show: the request never arrived. Telling
 * an operator "validation failed" when their wifi dropped sends them hunting
 * through a form that is fine.
 */
export function toApiError(error: unknown): ApiError {
  // IDEMPOTENT, and it has to be: `baseQueryWithAuth` already normalises every
  // failure, so `toApiError(query.error)` — which is what every surface writes —
  // was re-running the whole pipeline over an `ApiError`. It has no `data`, so the
  // envelope parse failed and the CODE was replaced by `unknown_error` while the
  // message survived. Nothing noticed until P10 branched on a code
  // (`target_outranks_actor`) and the branch silently never fired.
  if (isApiError(error)) {
    return error;
  }

  const candidate: UnknownError = isRecord(error) ? error : {};
  const status = candidate.status;

  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return { status: 0, code: NETWORK_ERROR_CODE, message: i18n.t('common:error.network') };
  }

  const httpStatus =
    typeof status === 'number'
      ? status
      : typeof candidate.originalStatus === 'number'
        ? candidate.originalStatus
        : 0;

  const envelope = ErrorEnvelopeSchema.safeParse(candidate.data);
  if (envelope.success) {
    const details = readDetails(envelope.data.error.details);
    return {
      status: httpStatus,
      code: envelope.data.error.code,
      message: envelope.data.message ?? i18n.t('common:error.unexpected'),
      ...(details === undefined ? {} : { details }),
    };
  }

  // A `SerializedError` (a thrown exception inside the query) carries only a
  // message; keep it, because it is usually the most specific thing available.
  const message =
    typeof candidate.message === 'string' && candidate.message !== ''
      ? candidate.message
      : i18n.t('common:error.unexpected');

  return { status: httpStatus, code: UNKNOWN_ERROR_CODE, message };
}
